import { isAxiosError } from 'axios';

export const DEFAULT_ERROR_MESSAGE = 'Something went wrong. Please try again.';

// API error body: { success: false, data: null, error: string, details?: [...] }.
// A few older routes still reply with { message } — handle both.
interface ApiErrorBody {
  error?: unknown;
  message?: unknown;
  details?: unknown;
}

function firstDetail(details: unknown): string | null {
  if (!Array.isArray(details) || details.length === 0) return null;
  const d: unknown = details[0];
  if (typeof d !== 'object' || d === null) return null;
  const { field, instancePath, message } = d as Record<string, unknown>;
  if (typeof message !== 'string' || !message) return null;
  const where =
    typeof field === 'string'
      ? field
      : typeof instancePath === 'string'
        ? instancePath.replace(/^\//, '')
        : '';
  return where ? `${where}: ${message}` : message;
}

function bodyMessage(body: unknown): string | null {
  if (typeof body !== 'object' || body === null) return null;
  const { error, message, details } = body as ApiErrorBody;
  let text: string | null = null;
  if (typeof error === 'string' && error) text = error;
  else if (typeof error === 'object' && error !== null) {
    const nested = (error as { message?: unknown }).message;
    if (typeof nested === 'string' && nested) text = nested;
  }
  if (!text && typeof message === 'string' && message) text = message;
  if (!text) return null;
  // "Validation failed" alone is useless — say which field and why
  const detail = firstDetail(details);
  return detail ? `${text} — ${detail}` : text;
}

/**
 * The message to show the user for a failed request: the API's own message when there
 * is one, otherwise `fallback`. Never surfaces axios's generic
 * "Request failed with status code 409".
 */
export function getErrorMessage(error: unknown, fallback: string = DEFAULT_ERROR_MESSAGE): string {
  if (isAxiosError(error)) {
    const fromApi = bodyMessage(error.response?.data);
    if (fromApi) return fromApi;
    if (!error.response) return 'Unable to reach the server. Check your connection and try again.';
    // Status-specific wording when the API sent no message of its own
    if (error.response.status === 429)
      return 'Too many requests — please wait a minute and try again.';
    if (error.response.status === 413)
      return 'That file is too large — each file can be up to 8 MB.';
    return fallback;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}
